import { Link } from 'react-router';
import { FEED_REACH } from '../../shared/region';
import { CONTACT } from '../features/landing/SiteChrome';
import { PolicyLayout } from '../features/landing/PolicyLayout';
import styles from '../features/landing/Landing.module.css';

export const POLICIES_UPDATED = 'September 27, 2026';

export function Privacy() {
  return (
    <PolicyLayout
      title="Privacy"
      updated={POLICIES_UPDATED}
      intro={
        <p>
          Roadrunner is built so you can know exactly what it knows about you. This page explains it in plain language.
        </p>
      }
    >
      <h2>What Roadrunner keeps</h2>
      <div className={styles.tableWrap}>
        <table>
          <thead>
            <tr>
              <th scope="col">What</th>
              <th scope="col">Why</th>
              <th scope="col">Who can see it</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Your email address</td>
              <td>To sign you in, and for the occasional note about your account, like a reminder to finish joining</td>
              <td>Only you, and the site’s administrator if you need help</td>
            </tr>
            <tr>
              <td>Your name and bio</td>
              <td>So neighbors know who they’re talking to</td>
              <td>Neighbors</td>
            </tr>
            <tr>
              <td>Your town, like “Catalina”</td>
              <td>A friendly label on your profile</td>
              <td>Neighbors</td>
            </tr>
            <tr>
              <td>Your home area (a rough area, about a few city blocks)</td>
              <td>Decides whose notes you see</td>
              <td>Never shown to members. The administrator can view it on an internal map for troubleshooting.</td>
            </tr>
            <tr>
              <td>Records of your location checks</td>
              <td>To spot fake accounts</td>
              <td>The administrator. See below for exactly what’s recorded.</td>
            </tr>
            <tr>
              <td>Your notes, replies, photos and thanks</td>
              <td>That’s the board</td>
              <td>Neighbors within {FEED_REACH} of your home</td>
            </tr>
            <tr>
              <td>Reports you make</td>
              <td>Keeping the board safe</td>
              <td>Moderators. The person you report isn’t told who reported them.</td>
            </tr>
            <tr>
              <td>People you block</td>
              <td>So you don’t see each other</td>
              <td>Only you</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2>Your location</h2>
      <p>
        Roadrunner asks for your location once, when you tap “Verify my location,” and your phone asks your permission
        first. The exact coordinates are used for a moment to work out your rough home area and your town, then thrown
        away. They are never stored or logged. Roadrunner doesn’t track your location in the background, and it never
        shows anyone your address, your street, or how far away you live.
      </p>
      <p>
        Each location check records the time, whether it succeeded, how precise your phone’s reading was, and the
        country and region your internet connection appears to come from, with a rough distance between that and the
        location you verified. That helps the administrator spot people pretending to live here.
      </p>
      <p>
        Photos from phones usually carry the exact spot where they were taken. Roadrunner removes that information on
        your phone before upload, and again on the server, so photos never reveal where they were taken.
      </p>

      <h2>What Roadrunner doesn’t do</h2>
      <ul>
        <li>It doesn’t sell your personal information.</li>
        <li>It doesn’t follow you around the web or use advertising cookies.</li>
        <li>It never shows your email address, your street, or your location to other members.</li>
      </ul>

      <h2>Services Roadrunner uses</h2>
      <ul>
        <li>
          <strong>Cloudflare</strong> hosts the site and stores photos. It provides the “are you a person?” check, and
          cookieless visit statistics: pages viewed, the site you came from, device type and country, with no personal
          identifiers. Cloudflare may use strictly necessary security cookies to protect the site from abuse.
        </li>
        <li>
          <strong>Supabase</strong> runs the database and sign-in, in the western United States. Its sign-in system
          keeps a security log of sign-in events, including IP addresses.
        </li>
        <li>
          <strong>Resend</strong> sends the sign-in emails.
        </li>
      </ul>
      <p>Your browser stores a sign-in token so you stay signed in between visits. Signing out removes it.</p>

      <h2>How long things are kept</h2>
      <ul>
        <li>Your information is kept for as long as you have an account.</li>
        <li>
          When you delete a note or reply, it disappears from the board right away. The text stays on file, visible only
          to moderators, so reports about it can still be reviewed. It’s erased when your account is deleted.
        </li>
        <li>
          When you delete your account, everything listed above is erased immediately, including your photos. Moderation
          records keep what action was taken, like a suspension, but no longer point to you. Anyone who already viewed
          one of your photos may have a copy in their browser’s cache for up to 12 hours. Routine backups may hold
          deleted information for a short time before they’re overwritten.
        </li>
      </ul>

      <h2>Your choices</h2>
      <ul>
        <li>Edit your name and bio, delete notes and replies, and block people at any time.</li>
        <li>
          Delete your account from the <Link to="/me">Me</Link> page. It’s permanent and takes effect immediately.
        </li>
        <li>
          Want a copy of your information, or have a question? <a href={`mailto:${CONTACT.email}`}>Email me</a>.
        </li>
      </ul>

      <h2>Who Roadrunner is for</h2>
      <p>Roadrunner is for adults 18 and older who live in the area it serves.</p>

      <h2>Changes</h2>
      <p>
        If this policy changes, this page will be updated and the date at the top will change. Significant changes will
        be announced on the site.
      </p>

      <h2>Contact</h2>
      <p>
        Roadrunner is run by Nicholas De Leon in Catalina, Arizona. Reach me at{' '}
        <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>.
      </p>
    </PolicyLayout>
  );
}
